import {
  ButtonWithIcon,
  useFrontEndEventTrackingService,
  useTriggerOnPressEnter,
} from '@clickcease/cc-client-components-lib'
import { faFloppyDisk, faPlusCircle } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { WrapperWithButtons } from '../../components/Elements/WrapperWithButtons/WrapperWithButtons'

import { useAddMicrosoftAudience } from './api/addMicrosoftAudience'
import { useGetMicrosoftAudiences } from './api/getMicrosoftAudiences'
import { AudienceFormsList } from './AudienceFormsList'
import { MicrosoftAudienceDescription } from './MicrosoftAudienceDescription'
import { AudienceTagSelection, defaultAudienceTag } from './MicrosoftAudienceModal.utils'

interface CampaignsModalProps {
  onOpenChange: () => void
  audienceId?: number
  isOpen?: boolean
}

export const MicrosoftAudienceModalContent = ({
  onOpenChange,
  audienceId,
  isOpen,
}: CampaignsModalProps) => {
  const { t } = useTranslation('PMPlatforms')
  const microsoftAudiencesQuery = useGetMicrosoftAudiences({})
  const { track } = useFrontEndEventTrackingService()
  const listRef = useRef<HTMLDivElement>(null)
  const isChanged = useRef(false)

  const audiencesData = useMemo(() => {
    if (!(microsoftAudiencesQuery.data instanceof Error) && microsoftAudiencesQuery.data) {
      return microsoftAudiencesQuery.data.audiences
    }
    return []
  }, [microsoftAudiencesQuery.data])

  const { mutate: addAudiences } = useAddMicrosoftAudience()
  const [audienceTags, setAudienceTags] = useState<AudienceTagSelection[]>([
    { ...defaultAudienceTag },
  ])

  useEffect(() => {
    if (audienceId) {
      setAudienceTags(audiencesData.filter((audience) => audienceId === audience.id))
    } else {
      setAudienceTags(audiencesData)
    }
  }, [audienceId, audiencesData])

  const addTag = () => {
    isChanged.current = true
    const newTags = [...audienceTags]
    newTags.push({ ...defaultAudienceTag })
    setAudienceTags(newTags)
    setTimeout(() => {
      listRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 100)
  }

  const onTagChange = (newTags: AudienceTagSelection[]) => {
    isChanged.current = true
    setAudienceTags(newTags)
  }

  const submitAudience = () => {
    isChanged.current = false
    addAudiences(audienceTags)
    track(['dataLayer', 'segment'], 'Microsoft.SaveConnection', {})
    onOpenChange()
  }

  useTriggerOnPressEnter(isOpen ? submitAudience : () => {})

  const isSaveDisabled =
    !isChanged.current ||
    (audienceTags?.length > 0 && audienceTags.some((tag) => !tag.uetTag || !tag.domainId))

  return (
    <WrapperWithButtons
      primaryButtonText={t('microsoft-audience-modal.primary-button')}
      primaryButtonIcon={<FontAwesomeIcon icon={faFloppyDisk as IconProp} />}
      isPrimaryButtonDisabled={isSaveDisabled}
      ghostButtonText={t('microsoft-audience-modal.ghost-button')}
      onPrimaryButtonClick={submitAudience}
      onGhostButtonClick={onOpenChange}
    >
      <MicrosoftAudienceDescription />
      <AudienceFormsList
        editMode={!!audienceId}
        tags={audienceTags.length > 0 ? audienceTags : [{ ...defaultAudienceTag }]}
        setTags={onTagChange}
        ref={listRef}
      />
      {!audienceId && (
        <ButtonWithIcon
          text={t('microsoft-audience-modal.add-tag-button')}
          onClick={addTag}
          iconLeft={<FontAwesomeIcon icon={faPlusCircle as IconProp} />}
          variant="ghost"
        />
      )}
    </WrapperWithButtons>
  )
}
